import { SITE_NAME, SITE_URL } from './meta'

import type { RouteMeta } from './meta'

export type JsonLdObject = Record<string, unknown>

const ORGANIZATION_ID = `${SITE_URL}/#organization`
const WEBSITE_ID = `${SITE_URL}/#website`

function absoluteUrl(path: string): string {
  const normalized = path === '/' ? '' : path.replace(/\/$/, '')
  return `${SITE_URL}${normalized}`
}

// Legal entity fields beyond name/locality (VAT/registration numbers, street address)
// aren't published anywhere in the repo - see docs/plan/cloudsoft-www-seo.md open items.
export function buildOrganization(): JsonLdObject {
  return {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: SITE_NAME,
    legalName: 'CloudSoft Marcin Malinowski',
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    email: 'contact@cloudsoft.com.pl',
    founder: { '@type': 'Person', name: 'Marcin Malinowski' },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Szczecin',
      addressCountry: 'PL'
    }
  }
}

export function buildWebSite(): JsonLdObject {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: SITE_NAME,
    url: SITE_URL,
    publisher: { '@id': ORGANIZATION_ID }
  }
}

export function buildHomeJsonLd(): JsonLdObject[] {
  return [buildOrganization(), buildWebSite()]
}

export function buildBreadcrumbList(items: { title: string; path: string }[]): JsonLdObject {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.title,
      item: absoluteUrl(item.path)
    }))
  }
}

export function buildBlogPosting(meta: RouteMeta): JsonLdObject {
  const post = meta.post
  if (!post) {
    throw new Error(`buildBlogPosting called for a route with no post: ${meta.path}`)
  }
  return {
    '@type': 'BlogPosting',
    headline: post.data.title,
    description: post.data.excerpt,
    url: absoluteUrl(meta.path),
    datePublished: post.data.created,
    dateModified: post.data.updated ?? post.data.created,
    keywords: post.data.tags.join(', '),
    author: { '@type': 'Person', name: post.data.author ?? 'Marcin Malinowski' },
    publisher: { '@id': ORGANIZATION_ID },
    ...(post.data.image ? { image: absoluteUrl(post.data.image) } : {})
  }
}

export function buildPostJsonLd(meta: RouteMeta): JsonLdObject[] {
  if (!meta.post) {
    return []
  }
  const breadcrumb = buildBreadcrumbList([
    { title: SITE_NAME, path: '/' },
    { title: 'Blog', path: '/posts' },
    { title: meta.post.data.title, path: meta.path }
  ])
  return [breadcrumb, buildBlogPosting(meta)]
}
